// Types
import {
  CampaignAppearanceType,
  CampaignMediaType,
  CampaignSmPlatform,
  CampaignTaskType,
  CampaignUsageRightsScope,
  CustomBriefProductInput,
  UserRole,
} from 'api/graphql-global-types';

export enum TalentType {
  Both = 'Both',
  Athlete = 'Athlete',
  Creator = 'Creator',
}

export const TALENT_TYPE_OPTIONS: {
  value: TalentType;
  label: string;
  roles: UserRole[];
}[] = [
  {
    value: TalentType.Both,
    label: 'Both',
    roles: [UserRole.Athlete, UserRole.ContentCreator],
  },
  { value: TalentType.Athlete, label: 'Athlete', roles: [UserRole.Athlete] },
  {
    value: TalentType.Creator,
    label: 'Creator',
    roles: [UserRole.ContentCreator],
  },
];

export const getTalentTypeFromRoles = (roles: UserRole[]): TalentType => {
  const hasAthlete = roles.includes(UserRole.Athlete);
  const hasCreator = roles.includes(UserRole.ContentCreator);
  if (hasAthlete && hasCreator) return TalentType.Both;
  if (hasCreator) return TalentType.Creator;
  return TalentType.Athlete;
};

export const AGE_RANGE_OPTIONS = [
  { value: '18-24', label: '18-24', minAge: 18, maxAge: 24 },
  { value: '25-34', label: '25-34', minAge: 25, maxAge: 34 },
  { value: '35-44', label: '35-44', minAge: 35, maxAge: 44 },
  { value: '45-54', label: '45-54', minAge: 45, maxAge: 54 },
  { value: '55-64', label: '55-64', minAge: 55, maxAge: 64 },
  { value: '65+', label: '65+', minAge: 65, maxAge: null },
];

export const parseAgePreset = (
  value: string
): { minAge: number; maxAge: number | null } => {
  if (value.endsWith('+')) return { minAge: parseInt(value), maxAge: null };
  const [min, max] = value.split('-').map(Number);
  return { minAge: min, maxAge: max ?? null };
};

export const CUSTOM_SOCIAL_PLATFORM_OPTIONS: {
  platform: CampaignSmPlatform;
  label: string;
}[] = [
  // Only Instagram & TikTok are supported for custom campaigns for now.
  { platform: CampaignSmPlatform.IG, label: 'Instagram' },
  { platform: CampaignSmPlatform.TT, label: 'TikTok' },
];

export const APPEARANCE_TYPE_OPTIONS: {
  value: CampaignAppearanceType;
  label: string;
}[] = [
  { label: 'On-site Filming', value: CampaignAppearanceType.OnSiteFilming },
  { label: 'Photoshoot', value: CampaignAppearanceType.Photoshoot },
  { label: 'Speaking', value: CampaignAppearanceType.Speaking },
  { label: 'Signing', value: CampaignAppearanceType.Signing },
  { label: 'Event Attendance', value: CampaignAppearanceType.EventAttendance },
  { label: 'Meet-and-greet', value: CampaignAppearanceType.MeetAndGreet },
  { label: 'Other', value: CampaignAppearanceType.Other },
];

export type CustomDeliverableCategory = 'Social Media' | 'UGC' | 'Appearance';

// Social deliverables only support Instagram & TikTok (mirrors millions-fe
// PLATFORM_OPTIONS). YouTube / X are talent-search filters, not post platforms.
export const SOCIAL_DELIVERABLE_PLATFORMS: CampaignSmPlatform[] = [
  CampaignSmPlatform.IG,
  CampaignSmPlatform.TT,
];

export const INSTAGRAM_POST_TYPE_OPTIONS: {
  label: string;
  value: CampaignTaskType;
}[] = [
  { label: 'Live', value: CampaignTaskType.IG_LIVE },
  { label: 'Post', value: CampaignTaskType.IG_POST },
  { label: 'Reel', value: CampaignTaskType.IG_REEL },
  { label: 'Story', value: CampaignTaskType.IG_STORY },
];

export const TIKTOK_POST_TYPE_OPTIONS: {
  label: string;
  value: CampaignTaskType;
}[] = [
  { label: 'Live', value: CampaignTaskType.TT_LIVE },
  { label: 'Post', value: CampaignTaskType.TT_VIDEO },
  { label: 'Story', value: CampaignTaskType.TT_STORY },
];

export const getCustomPostTypeOptions = (
  platform: CampaignSmPlatform
): { label: string; value: CampaignTaskType }[] =>
  platform === CampaignSmPlatform.TT
    ? TIKTOK_POST_TYPE_OPTIONS
    : INSTAGRAM_POST_TYPE_OPTIONS;

export const MEDIA_TYPE_OPTIONS: {
  label: string;
  value: CampaignMediaType;
}[] = [
  { label: 'Video', value: CampaignMediaType.VIDEO },
  { label: 'Image', value: CampaignMediaType.IMAGE },
];

const VIDEO_ONLY_OPTIONS: { label: string; value: CampaignMediaType }[] = [
  { label: 'Video', value: CampaignMediaType.VIDEO },
];

// Allowed raw content (media type) options per post type (mirrors millions-fe).
// Instagram: Reel/Live -> Video only; Story/Post -> Video + Image.
// TikTok: Live -> Video only; Post/Story -> Video + Image.
const MEDIA_TYPE_OPTIONS_BY_POST_TYPE: Partial<
  Record<CampaignTaskType, { label: string; value: CampaignMediaType }[]>
> = {
  [CampaignTaskType.IG_REEL]: VIDEO_ONLY_OPTIONS,
  [CampaignTaskType.IG_LIVE]: VIDEO_ONLY_OPTIONS,
  [CampaignTaskType.IG_STORY]: MEDIA_TYPE_OPTIONS,
  [CampaignTaskType.IG_POST]: MEDIA_TYPE_OPTIONS,
  [CampaignTaskType.TT_LIVE]: VIDEO_ONLY_OPTIONS,
  [CampaignTaskType.TT_VIDEO]: MEDIA_TYPE_OPTIONS,
  [CampaignTaskType.TT_STORY]: MEDIA_TYPE_OPTIONS,
};

// Returns the media types allowed for a given post type. Falls back to
// Video + Image when no post type is set yet.
export const getMediaTypeOptions = (
  postType?: CampaignTaskType | null
): { label: string; value: CampaignMediaType }[] =>
  (postType && MEDIA_TYPE_OPTIONS_BY_POST_TYPE[postType]) || MEDIA_TYPE_OPTIONS;

export const platformLabel = (platform: CampaignSmPlatform): string => {
  switch (platform) {
    case CampaignSmPlatform.IG:
      return 'Instagram';
    case CampaignSmPlatform.TT:
      return 'TikTok';
    case CampaignSmPlatform.YT:
      return 'YouTube';
    case CampaignSmPlatform.TW:
      return 'X';
    default:
      return platform;
  }
};

// Local UI shape for the dynamic per-platform follower / engagement filters.
export type PlatformCriteriaItem = {
  platform: CampaignSmPlatform;
  minFollowers: number | null;
  maxFollowers: number | null;
  minEngagementRate: number | null;
};

export enum PromoType {
  Code = 'Code',
  Link = 'Link',
}

export const EMPTY_PRODUCT: CustomBriefProductInput = {
  name: '',
  cost: 0,
  qty: 1,
  description: null,
  shipping: false,
};

export const USAGE_RIGHTS_SCOPE_OPTIONS = [
  { label: 'Social Repost', value: CampaignUsageRightsScope.SocialRepost },
  { label: 'Paid Media', value: CampaignUsageRightsScope.PaidMedia },
  { label: 'Website', value: CampaignUsageRightsScope.Website },
  { label: 'Whitelisting', value: CampaignUsageRightsScope.Whitelisting },
  { label: 'All Other Media', value: CampaignUsageRightsScope.AllOtherMedia },
];

export const APPLICATION_WINDOW_OPTIONS_DAYS = [14, 21, 28];

export const CONTRACT_ACCEPT_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];

export const normFile = (e: unknown): unknown => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && (e as { fileList?: unknown }).fileList;
};
